/*
  OK FBI — Public Custom Page renderer (route: /p/:pageId).
  - Fetches /api/pages/{pageId}.
  - If server returns {locked: true}, shows the PIN gate.
  - On successful unlock (server-side) refetches full blocks.
*/
import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Shell } from "@/components/Shell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Lock } from "lucide-react";

const toAbsUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  return `${process.env.REACT_APP_BACKEND_URL}${u.startsWith("/") ? u : "/" + u}`;
};

export default function CustomPageView() {
  const { pageId } = useParams();
  const [state, setState] = useState({ loading: true });
  const [pin, setPin] = useState("");
  const [unlocking, setUnlocking] = useState(false);

  const load = useCallback(async () => {
    setState({ loading: true });
    try {
      const { data } = await api.get(`/pages/${pageId}`);
      setState({ loading: false, page: data });
    } catch (e) {
      const s = e?.response?.status;
      setState({ loading: false, error: s === 403 ? "غير مسموح لك بعرض هذه الصفحة" :
                                       s === 404 ? "الصفحة غير موجودة" : "فشل التحميل" });
    }
  }, [pageId]);

  useEffect(() => { load(); }, [load]);

  const doUnlock = async (e) => {
    e?.preventDefault?.();
    if (!pin.trim()) return;
    setUnlocking(true);
    try {
      await api.post(`/pages/${pageId}/unlock`, { pin });
      toast.success("تم الفتح");
      setPin("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "رمز الدخول غير صحيح");
    } finally { setUnlocking(false); }
  };

  return (
    <Shell>
      <div className="min-h-screen p-6 max-w-[900px] mx-auto" dir="rtl"
           data-testid="custom-page-view">
        {state.loading && <div className="text-neutral-500 text-sm font-cairo">جاري التحميل…</div>}

        {state.error && (
          <div className="border border-red-900/40 bg-red-950/20 rounded p-6 text-center">
            <div className="text-red-300 font-cairo">{state.error}</div>
          </div>
        )}

        {state.page && state.page.locked && (
          <div data-testid="pin-gate"
               className="max-w-md mx-auto mt-16 border border-yellow-900/40 bg-neutral-950/70 rounded-lg p-8 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center mb-4">
              <Lock size={26} className="text-yellow-400"/>
            </div>
            <div className="text-[11px] tracking-[0.3em] text-yellow-500/70 font-cairo">صفحة محمية</div>
            <h1 className="text-2xl font-cairo font-black text-yellow-400 mt-1">{state.page.title || "صفحة سرية"}</h1>
            <p className="text-xs text-neutral-400 mt-2 font-cairo">
              هذه الصفحة محمية برمز دخول. أدخل الرمز الذي زوّدك به OWNER لعرض المحتوى.
            </p>
            <form onSubmit={doUnlock} className="mt-6 space-y-3">
              <input value={pin} onChange={e => setPin(e.target.value)}
                     placeholder="رمز الدخول"
                     data-testid="pin-input"
                     autoFocus type="password"
                     className="w-full bg-black/60 border border-neutral-800 rounded px-4 py-3 text-center text-lg font-mono tracking-widest"/>
              <button type="submit" disabled={unlocking}
                      data-testid="pin-submit"
                      className="w-full py-3 rounded bg-yellow-500 text-black font-cairo font-black disabled:opacity-50">
                {unlocking ? "…" : "فتح"}
              </button>
            </form>
          </div>
        )}

        {state.page && !state.page.locked && (
          <article data-testid="page-content" className="space-y-6">
            <header>
              <div className="text-[11px] tracking-[0.3em] text-yellow-500/70 font-cairo">أوكي إف بي آي</div>
              <h1 className="text-3xl font-cairo font-black text-yellow-400 mt-1">
                {state.page.title}
              </h1>
              {state.page.pin_required && (
                <div className="mt-1 text-[10px] text-neutral-500 font-cairo inline-flex items-center gap-1">
                  <Lock size={10}/> صفحة محمية · فُتحت مؤقتاً
                </div>
              )}
            </header>
            <div className="space-y-4">
              {(state.page.blocks || []).map((b) => <Block key={b.id} b={b}/>)}
            </div>
          </article>
        )}
      </div>
    </Shell>
  );
}

function Block({ b }) {
  if (b.type === "heading") {
    return <h2 className="text-2xl font-cairo font-black text-white">{b.text}</h2>;
  }
  if (b.type === "subheading") {
    return <h3 className="text-lg font-cairo font-black text-yellow-300 mt-4">{b.text}</h3>;
  }
  if (b.type === "text") {
    return <p className="text-sm font-cairo text-neutral-200 leading-7 whitespace-pre-wrap">{b.text}</p>;
  }
  if (b.type === "image" && b.url) {
    return (
      <figure>
        <img src={toAbsUrl(b.url)} alt={b.alt || ""}
             className="w-full max-h-[520px] object-cover rounded border border-neutral-800"/>
        {b.alt && <figcaption className="text-[10px] font-cairo text-neutral-500 text-center mt-1">{b.alt}</figcaption>}
      </figure>
    );
  }
  if (b.type === "video" && b.url) {
    const src = toAbsUrl(b.url);
    // Direct file uploads (mp4/webm/mov/…) or any /uploads/ path use the
    // native <video> tag so investigators get real playback controls.
    // Remote embeds (YouTube, Vimeo) still fall back to sandboxed iframe.
    const isDirect =
      /\.(mp4|webm|mov|m4v|ogg)$/i.test(b.url) || b.url.startsWith("/uploads/");
    if (isDirect) {
      return (
        <div className="aspect-video border border-neutral-800 rounded overflow-hidden bg-black">
          <video src={src} controls playsInline
                 className="w-full h-full object-contain"/>
        </div>
      );
    }
    return (
      <div className="aspect-video border border-neutral-800 rounded overflow-hidden">
        <iframe src={src} title="video"
                sandbox="allow-scripts allow-same-origin allow-presentation"
                allow="autoplay; encrypted-media; picture-in-picture"
                className="w-full h-full"/>
      </div>
    );
  }
  if (b.type === "button" && (b.label || b.url)) {
    return (
      <a href={b.url || "#"} target="_blank" rel="noreferrer"
         className="inline-block px-6 py-3 rounded bg-yellow-500 text-black font-cairo font-black text-sm hover:brightness-110">
        {b.label || "افتح"}
      </a>
    );
  }
  if (b.type === "divider") {
    return <hr className="border-neutral-800"/>;
  }
  return null;
}
